import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, OnInit, computed, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink, RouterOutlet } from '@angular/router';
import {
  AUDIT_FIELD_LABELS, CLOSING_TYPE_LABELS, CLOSING_TYPE_OPTIONS, EFFECTIVENESS_LABELS, INCIDENT_NATURE_LABELS,
  INTEGRATED_SYSTEM_LABELS, ORIGIN_DETAIL_LABELS, ORIGIN_ICONS, ORIGIN_LABELS, PARTICIPANT_ICONS,
  COMPLAINT_REASON_LABELS, IMPACT_LABELS, INTERESTED_PARTY_LABELS, NATURE_ICONS, NATURE_LABELS, REPORTER_NATURE_DETAIL_LABELS, REPORTER_NATURE_LABELS,
  ORGANIZATION_RELATION_LABELS, PROCESS_AREA_LABELS, REPORT_CATEGORY_LABELS, WORKER_TYPE_LABELS,
  PARTICIPANT_LABELS, SCOPE_ICONS, SCOPE_LABELS, SITUATION_LABELS, SITUATION_OPTIONS, STATE_LABELS,
  START_LABELS, STATE_OPTIONS, STATE_ORDER, SUB_SCOPE_LABELS, TYPE_ICONS, TYPE_LABELS, OUTCOME_LABELS, OUTCOME_OPTIONS,
  TRIAGE_RESULT_LABELS, LEGAL_DEADLINES, isSensitiveType, stateBadgeClass, stateBadgeLabel,
} from '../../../core/occurrence/occurrence.labels';
import {
  ClosingType, LinkedOccurrence, Occurrence, OccurrenceAuditEntry,
  OccurrenceConversationLink, OccurrenceLinks,
  MessageAudience, OccurrenceMessage, OccurrenceOutcome, OccurrenceSituation, OccurrenceState,
} from '../../../core/occurrence/occurrence.models';
import {
  CONSENT_CHANNEL_LABELS, CONSENT_STATUS_LABELS, DATA_SUBJECT_REQUEST_STATUS_CLASS,
  DATA_SUBJECT_REQUEST_STATUS_LABELS, RETENTION_BASIS_LABELS, labelOr,
} from '../../../core/occurrence/data-subject.labels';
import {
  DataSubjectLookup, DataSubjectRequest,
} from '../../../core/occurrence/data-subject.models';
import { MessageThread, groupThreads } from '../../../core/occurrence/message-threads';
import { OccurrenceService } from '../../../core/occurrence/occurrence.service';
import { AuthService } from '../../../core/auth/auth.service';
import { ApiError } from '../../../core/auth/auth.models';
import { ToastService } from '../../../core/ui/toast.service';
import { UserService } from '../../../core/user/user.service';
import { ConfirmDialog } from '../../../shared/confirm-dialog/confirm-dialog';
import { PageHeader } from '../../../shared/page-header/page-header';
import { DatePicker } from '../../../shared/date-picker/date-picker';
import { OccurrenceActions } from '../../../shared/occurrence-actions/occurrence-actions';
import { OccurrenceFollowUps } from '../../../shared/occurrence-follow-ups/occurrence-follow-ups';
import { OccurrenceEvidences } from '../../../shared/occurrence-evidences/occurrence-evidences';

function isoToday(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

@Component({
  selector: 'app-occurrence-detail',
  imports: [RouterLink, RouterOutlet, FormsModule, DatePipe, PageHeader, DatePicker,
    OccurrenceFollowUps, OccurrenceActions, OccurrenceEvidences],
  templateUrl: './occurrence-detail.html',
})
export class OccurrenceDetail implements OnInit {
  private readonly service = inject(OccurrenceService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toasts = inject(ToastService);
  protected readonly auth = inject(AuthService);
  private readonly users = inject(UserService);

  protected readonly occurrence = signal<Occurrence | null>(null);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);
  protected readonly changing = signal(false);
  protected readonly stateError = signal<string | null>(null);

  protected readonly typeLabels = TYPE_LABELS;
  protected readonly originLabels = ORIGIN_LABELS;
  protected readonly originDetailLabels = ORIGIN_DETAIL_LABELS;
  protected readonly participantLabels = PARTICIPANT_LABELS;
  protected readonly participantIcons = PARTICIPANT_ICONS;
  protected readonly incidentNatureLabels = INCIDENT_NATURE_LABELS;
  protected readonly natureLabels = NATURE_LABELS;
  protected readonly reporterNatureLabels = REPORTER_NATURE_LABELS;
  protected readonly reporterNatureDetailLabels = REPORTER_NATURE_DETAIL_LABELS;
  protected readonly natureIcons = NATURE_ICONS;
  protected readonly impactLabels = IMPACT_LABELS;
  protected readonly processAreaLabels = PROCESS_AREA_LABELS;
  protected readonly complaintReasonLabels = COMPLAINT_REASON_LABELS;
  protected readonly reportCategoryLabels = REPORT_CATEGORY_LABELS;
  protected readonly relationLabels = ORGANIZATION_RELATION_LABELS;
  protected readonly workerTypeLabels = WORKER_TYPE_LABELS;
  protected readonly interestedPartyLabels = INTERESTED_PARTY_LABELS;
  protected readonly scopeLabels = SCOPE_LABELS;
  protected readonly subScopeLabels = SUB_SCOPE_LABELS;
  protected readonly stateLabels = STATE_LABELS;
  protected readonly stateOptions = STATE_OPTIONS;
  protected readonly situationLabels = SITUATION_LABELS;
  protected readonly situationOptions = SITUATION_OPTIONS;
  protected readonly closingTypeLabels = CLOSING_TYPE_LABELS;
  protected readonly closingTypeOptions = CLOSING_TYPE_OPTIONS;
  protected readonly triageResultLabels = TRIAGE_RESULT_LABELS;
  protected readonly outcomeLabels = OUTCOME_LABELS;
  protected readonly outcomeOptions = OUTCOME_OPTIONS;
  protected readonly legalDeadlines = LEGAL_DEADLINES;

  /** Mensagens abertas na thread — fechadas por omissão, para a página se percorrer. */
  protected readonly expandedMessages = signal<ReadonlySet<number>>(new Set());

  protected toggleMessage(id: number): void {
    this.expandedMessages.update((open) => {
      const next = new Set(open);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }
  protected readonly effectivenessLabels = EFFECTIVENESS_LABELS;
  protected readonly scopeIcons = SCOPE_ICONS;
  protected readonly typeIcons = TYPE_ICONS;
  protected readonly originIcons = ORIGIN_ICONS;

  /** Ordem canónica do ciclo de vida, para o stepper de posição (o fecho é situação, não estado). */
  protected readonly stateFlow: OccurrenceState[] =
    ['REGISTADO', 'EM_ANALISE', 'EM_TRATAMENTO', 'EM_MONITORIZACAO', 'AVALIACAO_REAVALIACAO'];

  /**
   * Estados por onde a ocorrência passou de facto, lidos do histórico (o `oldValue` da primeira
   * mudança é o estado em que foi criada). Antes o stepper marcava como concluídos todos os passos
   * à esquerda do atual — uma ocorrência criada em Análise aparecia como se tivesse feito triagem.
   */
  protected readonly visitedStates = computed<ReadonlySet<OccurrenceState>>(() => {
    const visited = new Set<OccurrenceState>();
    const changes = this.historyEntries().filter((entry) => entry.field === 'state').reverse();
    if (changes.length && changes[0].oldValue) {
      visited.add(changes[0].oldValue as OccurrenceState);
    }
    for (const change of changes) {
      if (change.newValue) visited.add(change.newValue as OccurrenceState);
    }
    const current = this.occurrence();
    if (current) visited.add(current.state);
    return visited;
  });

  protected stepStatus(item: Occurrence, step: OccurrenceState): 'done' | 'current' | 'todo' {
    if (step === item.state) return 'current';
    return this.visitedStates().has(step) ? 'done' : 'todo';
  }

  protected newState: OccurrenceState | '' = '';
  protected newSituation: OccurrenceSituation = 'ABERTO';
  protected newClosingType: ClosingType | null = null;
  /** Desfecho escolhido no fecho — só as denúncias e reclamações o pedem. */
  protected newOutcome: OccurrenceOutcome | null = null;
  protected newClosingResult = '';

  /** Denúncias e reclamações: os casos do canal, com triagem e desfecho próprios. */
  protected readonly isCaseWork = computed(() => {
    const nature = this.occurrence()?.nature;
    return nature === 'DENUNCIA' || nature === 'RECLAMACAO';
  });
  protected newClosingDate = '';
  protected newReevaluationDeadline = '';

  private id = 0;

  /** "ISO 9001, NP 4590" para o subâmbito Sistema Integrado. */
  protected systemsLabel(occurrence: Occurrence): string {
    return occurrence.integratedSystems.map((system) => INTEGRATED_SYSTEM_LABELS[system]).join(', ');
  }

  /** Equipa a quem se pode partilhar — a mesma lista que serve de responsável pela ação. */
  protected readonly teamMembers = signal<{ id: number; name: string }[]>([]);

  // Comunicações — a thread de e-mails da ocorrência
  protected readonly messages = signal<OccurrenceMessage[]>([]);
  /** As mensagens em conversas, cada uma com o seu "Responder" (revisão de 2026-10-19). */
  protected readonly threads = computed(() => groupThreads(this.messages()));
  protected readonly messageError = signal<string | null>(null);
  protected readonly showMessageForm = signal(false);
  protected readonly sendingMessage = signal(false);
  protected newMessageTo = '';
  protected newMessageSubject = '';
  protected newMessageBody = '';
  /**
   * Externa leva o link da conversa; interna fica entre a equipa; a do portal espera na página
   * de acompanhamento, para pedir elementos a quem participou sem se identificar.
   */
  protected newMessageAudience: MessageAudience = 'EXTERNA';
  protected readonly conversationLinks = signal<OccurrenceConversationLink[]>([]);
  protected readonly copiedLink = signal<string | null>(null);

  // Titular dos dados (RGPD) — a ficha vive no Essentials; aqui fica o rasto dos pedidos
  protected readonly dataSubject = signal<DataSubjectLookup | null>(null);
  protected readonly dataSubjectLoading = signal(false);
  protected readonly dataSubjectRequests = signal<DataSubjectRequest[]>([]);
  protected readonly showErasureForm = signal(false);
  protected readonly requestingErasure = signal(false);
  protected readonly erasureError = signal<string | null>(null);
  protected erasureReason = '';

  protected readonly retentionBasisLabels = RETENTION_BASIS_LABELS;
  protected readonly consentStatusLabels = CONSENT_STATUS_LABELS;
  protected readonly consentChannelLabels = CONSENT_CHANNEL_LABELS;
  protected readonly requestStatusLabels = DATA_SUBJECT_REQUEST_STATUS_LABELS;
  protected readonly requestStatusClass = DATA_SUBJECT_REQUEST_STATUS_CLASS;
  protected readonly labelOr = labelOr;

  // Evidências — entradas com data, descrição, notas e ficheiros
  /** A secção Evidências — a Análise abre-lhe o registo, e o seguimento pode registar uma. */
  private readonly evidencesSection = viewChild<OccurrenceEvidences>('evidencesSection');
  /** O seguimento — relê as ações quando o tratamento muda, para saber se já se pode registar. */
  private readonly followUpsSection = viewChild<OccurrenceFollowUps>('followUps');
  /** As ações — o painel de edição também as acrescenta, e a ficha por baixo não sabia. */
  private readonly actionsSection = viewChild<OccurrenceActions>('actionsSection');
  /**
   * Sem ações no tratamento não há seguimento nem ocorrências derivadas (o servidor recusa a
   * ligação). Dizê-lo no botão evita preencher o formulário todo para ouvir a recusa no fim.
   */
  protected readonly hasActions = computed(() => (this.actionsSection()?.count() ?? 0) > 0);

  // Etapa 5 — ligações mãe↔filha e histórico de reclassificações
  protected readonly links = signal<OccurrenceLinks | null>(null);
  protected readonly historyEntries = signal<OccurrenceAuditEntry[]>([]);
  /** Acabou de ser fechada nesta visita: o card Ligações sugere abrir a ação de resposta. */
  protected readonly justClosed = signal(false);

  /**
   * O tratamento já começou? É a partir daí que faz sentido escrever seguimento — a mesma regra
   * do separador no formulário, dita aqui para a ficha não oferecer o que o outro lado recusa.
   */
  protected treatmentStarted(item: Occurrence): boolean {
    return STATE_ORDER.indexOf(item.state) >= STATE_ORDER.indexOf('EM_TRATAMENTO');
  }

  /** Denúncias e afins seguem circuito reservado — o detalhe abre com o aviso. */
  protected sensitive(item: Occurrence): boolean {
    return isSensitiveType(item.nature);
  }

  ngOnInit(): void {
    // Navegar entre ocorrências ligadas troca só o parâmetro — recarrega tudo.
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.id = Number(params.get('id'));
      // Um endereço escrito à mão com um id que não é número: nem se pergunta à API (dava 500).
      if (!Number.isInteger(this.id) || this.id <= 0) {
        this.occurrence.set(null);
        this.error.set('Ocorrência não encontrada.');
        this.loading.set(false);
        return;
      }
      this.error.set(null);
      // Os pedidos dependentes só saem depois de a ocorrência existir: um id inexistente
      // dava 5 pedidos paralelos, todos 404.
      this.load(true);
    });
    // Quem se pode escolher para receber a partilha de uma entrada de seguimento.
    this.users.team().subscribe({
      next: (list) => this.teamMembers.set(list
        .filter((user) => user.status === 'ACTIVE')
        .map((user) => ({ id: user.id, name: user.name }))),
      error: () => this.teamMembers.set([]),
    });
  }

  /**
   * Só nos pedidos de exercício de direitos: nas outras ocorrências não há titular a quem
   * chamar assim, e ir perguntar por uma pessoa ao Essentials seria ir buscar dados sem
   * fundamento. A consulta é sempre ao vivo — nada da ficha fica guardado aqui.
   */
  protected rightsRequest(occurrence: Occurrence): boolean {
    return occurrence.scope === 'RGPD' && occurrence.subScope === 'EXERCICIO_DIREITOS';
  }

  private loadDataSubject(): void {
    const occurrence = this.occurrence();
    if (!occurrence || !this.rightsRequest(occurrence)) {
      this.dataSubject.set(null);
      this.dataSubjectRequests.set([]);
      return;
    }
    this.dataSubjectLoading.set(true);
    this.service.dataSubject(this.id).subscribe({
      next: (lookup) => {
        this.dataSubject.set(lookup);
        this.dataSubjectLoading.set(false);
      },
      // Sem contacto de e-mail preenchido a API recusa: o cartão fica só com o histórico.
      error: () => {
        this.dataSubject.set(null);
        this.dataSubjectLoading.set(false);
      },
    });
    this.service.dataSubjectRequests(this.id).subscribe({
      next: (list) => this.dataSubjectRequests.set(list),
      error: () => this.dataSubjectRequests.set([]),
    });
  }

  protected openErasureForm(): void {
    this.erasureReason = '';
    this.erasureError.set(null);
    this.showErasureForm.set(true);
  }

  protected cancelErasureForm(): void {
    this.showErasureForm.set(false);
  }

  protected requestErasure(): void {
    if (!this.erasureReason.trim()) {
      this.erasureError.set('Descreva o fundamento do pedido.');
      return;
    }
    this.erasureError.set(null);
    this.requestingErasure.set(true);
    this.service.requestDataSubjectErasure(this.id, this.erasureReason.trim()).subscribe({
      next: () => {
        this.requestingErasure.set(false);
        this.showErasureForm.set(false);
        this.loadDataSubject();
        this.toasts.success('Pedido de eliminação enviado ao Essentials.');
      },
      error: (error) => {
        this.requestingErasure.set(false);
        // A tentativa falhada fica gravada do lado de lá do pedido: recarrega para a mostrar.
        this.loadDataSubject();
        this.erasureError.set(error?.error?.message ?? 'Não foi possível pedir a eliminação.');
      },
    });
  }

  private loadLinks(): void {
    this.service.links(this.id).subscribe({
      next: (links) => this.links.set(links),
      error: () => this.links.set(null),
    });
  }

  private loadHistory(): void {
    this.service.history(this.id).subscribe({
      next: (entries) => this.historyEntries.set(entries),
      error: () => this.historyEntries.set([]),
    });
  }

  protected readonly auditFieldLabels = AUDIT_FIELD_LABELS;

  /** Traduz o valor bruto do histórico (nomes de enum) para o rótulo do utilizador. */
  protected auditValue(field: string, value: string | null): string {
    if (!value) return '—';
    if (field === 'integratedSystems') {
      return value.split(',')
        .map((system) => INTEGRATED_SYSTEM_LABELS[system as keyof typeof INTEGRATED_SYSTEM_LABELS] ?? system)
        .join(', ');
    }
    const dictionaries: Record<string, string>[] = [
      SCOPE_LABELS, SUB_SCOPE_LABELS, TYPE_LABELS, ORIGIN_LABELS, ORIGIN_DETAIL_LABELS,
      PARTICIPANT_LABELS, STATE_LABELS, SITUATION_LABELS, CLOSING_TYPE_LABELS,
      NATURE_LABELS, PROCESS_AREA_LABELS, OUTCOME_LABELS, TRIAGE_RESULT_LABELS,
    ];
    for (const dictionary of dictionaries) {
      if (value in dictionary) return dictionary[value];
    }
    return value;
  }

  protected linkBadgeClass(link: LinkedOccurrence): string {
    return stateBadgeClass(link);
  }

  protected linkBadgeLabel(link: LinkedOccurrence): string {
    return stateBadgeLabel(link);
  }

  /**
   * O painel de edição fechou. As ações, o seguimento e as evidências têm componentes próprios,
   * que só liam ao nascer: uma ação acrescentada no painel não aparecia na ficha, e o seguimento
   * continuava a dizer que faltavam ações — só "acordava" à segunda ação, registada já na ficha
   * (revisão de 2026-10-19).
   */
  protected reloadAfterEdit(): void {
    this.actionsSection()?.reload();
    this.followUpsSection()?.reload();
    this.evidencesSection()?.reload();
    this.reload();
  }

  /** O seguimento mexeu: uma entrada pode transferir o responsável e entra no histórico. */
  protected reload(): void {
    // A recusa de há pouco pode já não ser verdade: acrescentar a ação que faltava deixava o
    // painel do estado a pedi-la na mesma, em vermelho, sem nada por fazer.
    this.stateError.set(null);
    this.load();
    this.loadHistory();
  }

  private loadMessages(): void {
    this.service.messages(this.id).subscribe({
      next: (list) => this.messages.set(list),
      error: () => this.messages.set([]),
    });
    this.service.conversations(this.id).subscribe({
      next: (list) => this.conversationLinks.set(list),
      error: () => this.conversationLinks.set([]),
    });
  }

  protected copyLink(link: string): void {
    navigator.clipboard?.writeText(link).then(() => {
      this.copiedLink.set(link);
      setTimeout(() => this.copiedLink.set(null), 2000);
    });
  }

  /** O assunto abre já com o código: é ele que traz a resposta de volta a esta ocorrência. */
  protected openMessageForm(): void {
    const occurrence = this.occurrence();
    this.newMessageTo = occurrence?.participantEmail ?? '';
    this.newMessageSubject = occurrence ? `[${occurrence.code}] ${occurrence.title}` : '';
    this.newMessageBody = '';
    this.newMessageAudience = 'EXTERNA';
    this.messageError.set(null);
    this.showMessageForm.set(true);
  }

  /**
   * Responder dentro da conversa: o mesmo assunto — é ele que junta a resposta a esta conversa e
   * traz a seguinte de volta —, para a outra parte e pelo mesmo caminho.
   */
  protected replyTo(thread: MessageThread): void {
    this.newMessageTo = thread.replyTo ?? '';
    this.newMessageSubject = thread.subject;
    this.newMessageBody = '';
    this.newMessageAudience = thread.audience;
    this.messageError.set(null);
    this.showMessageForm.set(true);
    setTimeout(() => document.getElementById('form-comunicacao')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  }

  protected cancelMessageForm(): void {
    this.showMessageForm.set(false);
  }

  protected sendMessage(): void {
    // A do portal não vai para lado nenhum: fica à espera na página de acompanhamento.
    const portal = this.newMessageAudience === 'PORTAL';
    if ((!portal && !this.newMessageTo.trim()) || !this.newMessageSubject.trim() || !this.newMessageBody.trim()) {
      this.messageError.set(portal
        ? 'Preencha o assunto e a mensagem.'
        : 'Preencha o destinatário, o assunto e a mensagem.');
      return;
    }
    this.messageError.set(null);
    this.sendingMessage.set(true);
    this.service.sendMessage(this.id, {
      to: portal ? null : this.newMessageTo.trim(),
      subject: this.newMessageSubject.trim(),
      body: this.newMessageBody.trim(),
      audience: this.newMessageAudience,
    }).subscribe({
      next: () => {
        this.sendingMessage.set(false);
        this.showMessageForm.set(false);
        this.loadMessages();
        this.toasts.success(portal ? 'Mensagem publicada no acompanhamento.' : 'E-mail enviado.');
      },
      error: (error) => {
        this.sendingMessage.set(false);
        this.messageError.set(error?.error?.message ?? 'Não foi possível enviar o e-mail.');
      },
    });
  }

  /**
   * "Quando denúncia ou reclamação, deverá gerar o ID e informar o comunicante por email de
   * acordo com os modelos enviados" (documento de 2026-10-08).
   *
   * <p>Pelo site e pelo Essentials o pisco vem ligado — o identificador nasce com o registo. A
   * que a equipa regista à mão ficava sem ele, e quem comunicou não tinha por onde seguir o
   * caso. Liga-se uma vez e não se desliga: dado o identificador, ele é aquele.
   */
  protected readonly startingTracking = signal(false);

  protected startTracking(): void {
    const item = this.occurrence();
    if (!item || item.trackingCode) return;
    this.startingTracking.set(true);
    this.service.startTracking(item.id).subscribe({
      next: (updated) => {
        this.startingTracking.set(false);
        this.occurrence.set(updated);
        this.toasts.success(
          `Identificador ${updated.trackingCode} gerado. Quem comunicou foi avisado por e-mail.`);
      },
      error: () => {
        this.startingTracking.set(false);
        this.toasts.error('Não foi possível dar acompanhamento a esta ocorrência.');
      },
    });
  }

  /**
   * "Registar evidência" a partir da Análise (documento de 2026-10-08): a evidência que se
   * descobre a analisar fica onde as evidências vivem, mas regista-se sem ter de ir à
   * procura da secção no fundo da página.
   */
  protected registerEvidenceFromAnalysis(): void {
    this.evidencesSection()?.openForm();
    setTimeout(() => document.getElementById('seccao-evidencias')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }

  /** O tratamento mudou: o seguimento depende das ações (revisão de 2026-10-15). */
  protected onActionsChanged(): void {
    this.followUpsSection()?.reload();
    this.reload();
  }

  /** O seguimento mudou: pode ter trazido evidências, e mexido no responsável. */
  protected onFollowUpsChanged(): void {
    this.evidencesSection()?.reload();
    // Associar uma ação no seguimento põe-na Em seguimento (revisão de 2026-10-19).
    this.actionsSection()?.reload();
    this.reload();
  }

  private load(withDependents = false): void {
    // Só outra ocorrência mostra "A carregar…". Recarregar a mesma depois de gravar trocava a
    // ficha inteira pelo aviso: a página encolhia, o scroll voltava ao topo e os cartões nasciam
    // de novo ("retorna sempre ao início da ficha", revisão de 2026-10-13).
    if (this.occurrence()?.id !== this.id) this.loading.set(true);
    this.service.get(this.id).subscribe({
      next: (occurrence) => {
        const previous = this.occurrence();
        this.occurrence.set(occurrence);
        // O painel do estado só se repõe quando o estado mudou: registar um seguimento a meio
        // de escrever o fecho não pode levar o que lá se escreveu.
        if (previous?.id !== occurrence.id || previous.state !== occurrence.state
            || previous.situation !== occurrence.situation) {
          this.newState = occurrence.state;
          this.newSituation = occurrence.situation;
          this.syncStateFields(occurrence);
        }
        this.loading.set(false);
        if (withDependents) {
          this.loadMessages();
          this.loadLinks();
          this.loadHistory();
        }
        // Depende do âmbito, por isso só depois de saber qual é.
        this.loadDataSubject();
      },
      error: () => {
        this.error.set('Ocorrência não encontrada.');
        this.loading.set(false);
      },
    });
  }

  /**
   * Pré-preenche os campos do fecho ao trocar a seleção de estado ou de situação — só os que
   * estão vazios.
   *
   * <p>Repunha-os todos com o que estava gravado, e quem escrevia o resultado, recebia uma
   * recusa e mudava o estado para tentar outra vez ficava sem o texto (revisão de 2026-10-12).
   */
  protected onStateSelected(): void {
    this.stateError.set(null);
    if (this.newSituation !== 'FECHADO') return;
    const item = this.occurrence();
    // Com a verificação da eficácia aplicável, o fecho proposto é "Sujeito a verificação": ela
    // faz-se uns seis meses depois. Só propõe — troca-se.
    const proposedType = item && item.effectivenessEvaluation !== 'NAO_APLICAVEL'
      ? 'SUJEITO_VERIFICACAO' : null;
    this.newClosingType ??= item?.closingType ?? proposedType;
    if (!this.newClosingResult.trim()) this.newClosingResult = item?.closingResult ?? '';
    this.newClosingDate ||= item?.closingDate ?? isoToday();
    this.newOutcome ??= item?.outcome ?? null;
  }

  private syncStateFields(occurrence: Occurrence): void {
    this.newClosingType = occurrence.closingType;
    this.newOutcome = occurrence.outcome;
    this.newClosingResult = occurrence.closingResult ?? '';
    this.newClosingDate = occurrence.closingDate ?? '';
    this.newReevaluationDeadline = occurrence.reevaluationDeadline ?? '';
  }

  /**
   * O botão que põe a ocorrência no estado de uma secção, quando ela ainda lá não chegou.
   *
   * <p>É o mesmo que o formulário tem no rodapé, aqui no cabeçalho do cartão: é na ficha que se
   * lê o que já ficou feito e se decide avançar, e mandar a pessoa ao painel "Mudar estado" do
   * outro lado do ecrã para isso era um desvio sem razão (documento de 2026-10-06).
   */
  protected startLabel(item: Occurrence, state: OccurrenceState): string | null {
    if (item.situation === 'FECHADO') return null;
    // Só o estado logo a seguir. Mais à frente o botão existiria para ser recusado: passar a Em
    // avaliação com uma ocorrência acabada de registar não é um atalho, é um erro à espera.
    if (STATE_ORDER.indexOf(state) !== STATE_ORDER.indexOf(item.state) + 1) return null;
    return START_LABELS[state] ?? null;
  }

  /** A secção que cada estado abre — o botão "Iniciar…" leva lá, já com os campos à frente. */
  private static readonly STEP_OF_STATE: Partial<Record<OccurrenceState, string>> = {
    EM_ANALISE: 'analise',
    EM_TRATAMENTO: 'tratamento',
    EM_MONITORIZACAO: 'monitorizacao',
    AVALIACAO_REAVALIACAO: 'eficacia',
  };

  /** Secção a abrir assim que a mudança de estado passar. */
  private abrirSeccao: string | null = null;

  protected startState(state: OccurrenceState): void {
    this.newState = state;
    this.newSituation = 'ABERTO';
    // Mudar o estado e deixar a pessoa na ficha era metade do caminho: quem inicia uma etapa
    // quer preenchê-la (documento de 2026-10-08).
    this.abrirSeccao = OccurrenceDetail.STEP_OF_STATE[state] ?? null;
    this.applyState();
  }

  protected applyState(): void {
    if (!this.newState) return;
    const closing = this.newSituation === 'FECHADO';
    if (closing && !this.newClosingType) {
      this.stateError.set('Indique se o fecho foi com ou sem sucesso.');
      return;
    }
    if (closing && !this.newOutcome) {
      this.stateError.set('Escolha o desfecho do caso.');
      return;
    }
    // O resultado só se exige com o desfecho "Outro" (revisão de 2026-10-19).
    if (closing && this.newOutcome === 'OUTRO' && !this.newClosingResult.trim()) {
      this.stateError.set('Descreva o resultado com que a ocorrência foi fechada.');
      return;
    }
    // Fechar é o fim do ciclo. As saídas são para os casos que não chegam a ser tratados: um que
    // não é para aqui, ou um que ficou sem elementos, encerra-se onde estiver; e o que a triagem
    // recusa fecha-se na análise.
    const earlyClosing = this.newOutcome === 'NAO_ACEITE_NESTE_CANAL'
      || this.newOutcome === 'ENCERRADO_FALTA_ELEMENTOS'
      || this.newClosingType === 'FECHADO_APOS_ANALISE';
    // Sem verificação da eficácia, o ciclo acaba em Em seguimento (revisão de 2026-10-12).
    const effectivenessNotApplicable = this.occurrence()?.effectivenessEvaluation === 'NAO_APLICAVEL';
    const endOfCycle = this.newState === 'AVALIACAO_REAVALIACAO'
      || (this.newState === 'EM_MONITORIZACAO' && effectivenessNotApplicable);
    if (closing && !endOfCycle && !earlyClosing) {
      this.stateError.set((effectivenessNotApplicable
        ? 'Só se fecha a partir de Em seguimento ou de Em avaliação'
        : 'Só se fecha a partir de Em avaliação')
        + ' — ou feche-a na análise, com o tipo "fechado após análise", ou com um desfecho de '
        + '"não aceite neste canal" ou "encerrado por falta de elementos".');
      return;
    }
    if (this.occurrence()?.state === 'REGISTADO' && this.newState === 'EM_TRATAMENTO') {
      this.stateError.set('De Registado não se passa direto a Em tratamento: passe primeiro por '
        + 'Em análise.');
      return;
    }
    this.stateError.set(null);
    this.changing.set(true);
    const wasClosed = this.occurrence()?.situation === 'FECHADO';
    this.service.changeState(this.id, {
      state: this.newState,
      situation: this.newSituation,
      closingType: closing ? this.newClosingType : null,
      closingResult: closing ? this.newClosingResult.trim() || null : null,
      outcome: closing ? this.newOutcome : null,
      closingDate: closing ? this.newClosingDate || null : null,
      // O prazo de reavaliação saiu do ecrã (documento de 2026-10-08): devolve-se o que lá
      // estava, para a mudança de estado não apagar o histórico de quem já o tinha.
      reevaluationDeadline: this.newReevaluationDeadline || null,
      effectivenessEvaluationDate: null,
    }).subscribe({
      next: (occurrence) => {
        this.occurrence.set(occurrence);
        // Fechar fecha as ações (revisão de 2026-10-19); a tabela continuava com o estado antigo
        // até se recarregar a ficha (QA de 2026-10-21).
        this.actionsSection()?.reload();
        this.newState = occurrence.state;
        this.newSituation = occurrence.situation;
        this.syncStateFields(occurrence);
        this.changing.set(false);
        this.loadHistory();
        this.loadLinks();
        this.toasts.success(occurrence.situation === 'FECHADO'
          ? 'Ocorrência fechada.'
          : `Estado alterado para ${STATE_LABELS[occurrence.state]}.`);
        // Bloco F: "abrir nova ocorrência para responder a esta?" — em vez da janela nativa que
        // aparecia uma única vez, o card Ligações mostra a sugestão e a ação fica sempre lá.
        this.justClosed.set(occurrence.situation === 'FECHADO' && !wasClosed);
        const seccao = this.abrirSeccao;
        this.abrirSeccao = null;
        if (seccao) {
          this.router.navigate(['editar'], { relativeTo: this.route, queryParams: { step: seccao } });
        }
      },
      // O servidor diz o que falta, campo a campo: engolir isso deixava a pessoa a olhar para
      // "não foi possível" sem saber o que fazer — a queixa do documento de 2026-09-26.
      error: (response: HttpErrorResponse) => {
        const error = response.error as ApiError | undefined;
        const missing = (error?.fieldErrors ?? []).map((field) => field.message);
        const message = missing.length
          ? `${error?.message ?? 'Faltam campos obrigatórios.'} ${missing.join(' ')}`
          : error?.message ?? 'Não foi possível mudar o estado.';
        this.abrirSeccao = null;
        this.stateError.set(message);
        // Os botões "Iniciar…" dos cartões mudam o estado de longe do painel que mostra o erro.
        this.toasts.error(message);
        this.changing.set(false);
      },
    });
  }

  protected badgeClass(item: Occurrence): string {
    return stateBadgeClass(item);
  }

  protected stateBadgeLabel(item: Occurrence): string {
    return stateBadgeLabel(item);
  }
}
